import type {
  RegistryWidgetsType,
  TemplatesType,
  FieldTemplateProps,
  ObjectFieldTemplateProps,
  ArrayFieldTemplateProps,
  WidgetProps,
  FieldsType,
} from '@rjsf/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select as ShadcnSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import React from 'react';
import SliderWidget from './widgets/Slider';
import CodeEditorWidget from './widgets/CodeEditor';
import JsonField from './fields/JsonField';

//
// Widgets
//

function TextWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, onBlur, onFocus, placeholder, schema, options } = props;
  const emptyValue = (options as any)?.emptyValue;

  const placeholderFromExamples =
    placeholder ||
    (Array.isArray(schema?.examples) && schema.examples.length > 0 ? String(schema.examples[0]) : undefined);

  return (
    <Input
      id={id}
      value={(value as any) ?? ''}
      disabled={disabled || readonly}
      placeholder={placeholderFromExamples}
      onChange={(e) => onChange(e.target.value === '' ? emptyValue : e.target.value)}
      onBlur={onBlur && ((e) => onBlur(id, e.target.value))}
      onFocus={onFocus && ((e) => onFocus(id, e.target.value))}
    />
  );
}

function TextareaWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, onBlur, onFocus, placeholder, options } = props;
  const emptyValue = (options as any)?.emptyValue;
  const rows = (options as any)?.rows ?? 4;

  return (
    <textarea
      id={id}
      value={(value as any) ?? ''}
      disabled={disabled || readonly}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? emptyValue : e.target.value)}
      onBlur={onBlur && ((e) => onBlur(id, e.target.value))}
      onFocus={onFocus && ((e) => onFocus(id, e.target.value))}
      rows={rows}
      className="border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground aria-invalid:outline-destructive/60 aria-invalid:ring-destructive/20 dark:aria-invalid:outline-destructive dark:aria-invalid:ring-destructive/50 ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 aria-invalid:outline-destructive/60 dark:aria-invalid:outline-destructive dark:aria-invalid:ring-destructive flex w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:focus-visible:ring-[3px] aria-invalid:focus-visible:outline-none md:text-sm dark:aria-invalid:focus-visible:ring-4"
    />
  );
}

function CheckboxWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, label } = props;
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        checked={!!value}
        disabled={disabled || readonly}
        onChange={(e) => onChange(Boolean(e.target.checked))}
        className="size-4 rounded border"
      />
      {label ? <Label htmlFor={id}>{label}</Label> : null}
    </div>
  );
}

function NumberWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, onBlur, onFocus, placeholder, schema, options } = props;
  const min = typeof (schema as any)?.minimum === 'number' ? (schema as any).minimum : undefined;
  const max = typeof (schema as any)?.maximum === 'number' ? (schema as any).maximum : undefined;
  const step = (options as any)?.step ?? (schema as any)?.multipleOf ?? undefined;

  return (
    <Input
      id={id}
      type="number"
      value={(value as any) ?? ''}
      disabled={disabled || readonly}
      placeholder={placeholder}
      min={min as any}
      max={max as any}
      step={step as any}
      onChange={(e) => {
        const s = e.target.value;
        if (s === '') {
          onChange((options as any)?.emptyValue ?? undefined);
          return;
        }
        const n = Number(s);
        if (Number.isNaN(n)) {
          onChange((options as any)?.emptyValue ?? undefined);
        } else {
          onChange(n);
        }
      }}
      onBlur={onBlur && ((e) => onBlur(id, e.currentTarget.value))}
      onFocus={onFocus && ((e) => onFocus(id, e.currentTarget.value))}
    />
  );
}

function SelectWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, options, placeholder } = props;
  const enumOptions = (options.enumOptions ?? []) as Array<{ label: string; value: unknown }>;
  const isMultiple = Boolean((options as any)?.multiple);

  const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  const fromStr = (s: string) => {
    const hit = enumOptions.find((o) => String(o.value) === s);
    return hit ? hit.value : s;
  };

  if (isMultiple) {
    // Radix Select is single-select; use native <select multiple> for array enums.
    const current = Array.isArray(value) ? value.map(toStr) : [];
    return (
      <select
        id={id}
        multiple
        value={current}
        disabled={disabled || readonly}
        onChange={(e) => {
          const selections = Array.from(e.currentTarget.selectedOptions).map((o) => fromStr(o.value));
          onChange(selections);
        }}
        className="w-full rounded border px-3 py-2 text-sm"
        style={{ minHeight: 120 }}
      >
        {enumOptions.map((opt) => (
          <option
            key={toStr(opt.value)}
            value={toStr(opt.value)}
            disabled={(options.enumDisabled || []).includes(opt.value as any)}
          >
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  // Single select with Radix shadcn Select
  const EMPTY_SENTINEL = '__EMPTY__';
  const current = value === null || value === undefined ? undefined : toStr(value);
  return (
    <ShadcnSelect
      value={current as string | undefined}
      onValueChange={(v) => {
        if (v === EMPTY_SENTINEL && (options as any).emptyValue !== undefined) onChange((options as any).emptyValue);
        else if (v === EMPTY_SENTINEL) onChange(undefined);
        else onChange(fromStr(v));
      }}
      disabled={disabled || readonly}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {placeholder && <SelectItem value={EMPTY_SENTINEL}>{placeholder}</SelectItem>}
        {enumOptions.map((opt) => (
          <SelectItem
            key={toStr(opt.value)}
            value={toStr(opt.value)}
            disabled={(options.enumDisabled || []).includes(opt.value as any)}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </ShadcnSelect>
  );
}

function SegmentedWidget(props: WidgetProps) {
  const { id, value, disabled, readonly, onChange, options } = props;
  const enumOptions = (options.enumOptions ?? []) as Array<{ label: string; value: unknown }>;
  const isDisabled = disabled || readonly;

  const toStr = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  const fromStr = (s: string) => {
    const hit = enumOptions.find((o) => String(o.value) === s);
    return hit ? hit.value : s;
  };

  const current = value === null || value === undefined ? undefined : toStr(value);
  const isEnumDisabled = (val: unknown) => (options.enumDisabled || []).includes(val as any);

  return (
    <div id={id} role="group" className="inline-flex items-center gap-1">
      {enumOptions.map((opt) => {
        const valStr = toStr(opt.value);
        const selected = current === valStr;
        return (
          <Button
            key={valStr}
            type="button"
            variant={selected ? 'default' : 'outline'}
            size="sm"
            aria-pressed={selected}
            onClick={() => onChange(fromStr(valStr))}
            disabled={isDisabled || isEnumDisabled(opt.value)}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

//
// Field template
//

function renderSupportText(content: React.ReactNode, className: string) {
  if (content === null || content === undefined || content === false) {
    return null;
  }
  if (typeof content === 'string' || typeof content === 'number') {
    return <p className={className}>{content}</p>;
  }
  return <div className={className}>{content}</div>;
}

function FieldTemplate(props: FieldTemplateProps) {
  const { id, label, children, rawErrors = [], description, help, hidden } = props;

  if (hidden) {
    return <div style={{ display: 'none' }}>{children}</div>;
  }

  const hasError = rawErrors.length > 0;

  return (
    <div className="mb-4">
      {label ? (
        <Label htmlFor={id} className={hasError ? 'text-destructive' : undefined}>
          {label}
        </Label>
      ) : null}
      <div className="mt-2">{children}</div>
      {renderSupportText(description, 'mt-1 text-sm text-muted-foreground')}
      {renderSupportText(help, 'mt-1 text-sm text-muted-foreground')}
      {hasError && (
        <ul className="mt-1 text-sm text-destructive">
          {rawErrors.map((err, i) => (
            <li key={i}>{err}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ArrayFieldTemplate(props: ArrayFieldTemplateProps) {
  const { title, items, canAdd, onAddClick, readonly, disabled } = props as any;
  const description = (props as any)?.description as React.ReactNode | undefined;

  return (
    <div className="space-y-2">
      {title ? <div className="text-sm font-medium">{title}</div> : null}
      {renderSupportText(description, 'text-sm text-muted-foreground')}

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.key} className="rounded-md border p-3">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">{item.children}</div>
              <div className="flex items-center gap-2">
                {item.hasMoveUp && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={item.onReorderClick(item.index, item.index - 1)}
                    disabled={disabled || readonly}
                  >
                    Up
                  </Button>
                )}
                {item.hasMoveDown && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={item.onReorderClick(item.index, item.index + 1)}
                    disabled={disabled || readonly}
                  >
                    Down
                  </Button>
                )}
                {item.hasRemove && (
                  <Button
                    variant="destructive"
                    size="sm"
                    type="button"
                    onClick={item.onDropIndexClick(item.index)}
                    disabled={disabled || readonly}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {canAdd && (
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={onAddClick}
          disabled={disabled || readonly}
        >
          Add item
        </Button>
      )}
    </div>
  );
}

export const shadcnAdapter = {
  widgets: {
    // class-style keys (override defaults)
    TextWidget,
    TextareaWidget,
    SelectWidget,
    CheckboxWidget,
    UpDownWidget: NumberWidget,
    SegmentedWidget,
    // short keys used by ui:widget
    text: TextWidget,
    textarea: TextareaWidget,
    select: SelectWidget,
    checkbox: CheckboxWidget,
    updown: NumberWidget,
    segmented: SegmentedWidget,
    slider: SliderWidget,
    // code editors
    code: CodeEditorWidget,
    json: CodeEditorWidget,
  } satisfies RegistryWidgetsType,
  templates: {
    FieldTemplate,
    ArrayFieldTemplate,
    ObjectFieldTemplate: function ObjectFieldTemplate(props: ObjectFieldTemplateProps) {
      const { title, description, properties, canExpand, onAddClick, schema, disabled, readonly } =
        props as any;
      return (
        <div className="space-y-2">
          {title ? <div className="text-sm font-medium">{title}</div> : null}
          {renderSupportText(description, 'text-sm text-muted-foreground')}
          <div className="grid gap-3">
            {properties.map((prop) => (prop.hidden ? null : <div key={prop.name}>{prop.content}</div>))}
          </div>
          {canExpand && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={onAddClick(schema)}
              disabled={disabled || readonly}
            >
              Add property
            </Button>
          )}
        </div>
      );
    },
  } as TemplatesType,
  formOverrides: {
    // Optionally, set className: 'space-y-4'
  },
  fields: {
    json: JsonField,
    code: JsonField,
  } satisfies FieldsType,
};

export type ShadcnAdapter = typeof shadcnAdapter;
